import { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../../shared/grid/usePersistedGridState';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { describeApiError } from '../../../../shared/api/errors';
import { useToast } from '../../../../shared/stores/toast.store';
import { companiesHooks } from '../../hooks';
import { useCompanyDocumentosFiscaisHooks } from '../../documentos-fiscais/hooks';
import { usePendentesFaturamento, useNotasMae } from '../hooks';
import { PreviewAutomaticaDialog } from '../PreviewAutomaticaDialog';
import { ManualDialog } from '../ManualDialog';
import type { NotaFiscalColabPendenteItem, NotaFiscalColabPendenteStatus } from '../types';

/**
 * Modelo NF fixo, populado no start do webpanel:
 *   &ModeloNotaFiscal = ModeloNotaFiscal.RPSNFSe   (readOnly no WWP)
 * Só a série varia por empresa. Confirmação do enum vs valor numérico pendente
 * de resposta da Genie no card [m1O7KeaU].
 */
const MODELO_NF_FIXO = '65';
const MODELO_NF_LABEL = 'RPS/NFS-e';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'colaboradorNome',
  sortOrder: 'asc',
};

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function formatHoras(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusColor(
  s: NotaFiscalColabPendenteStatus,
): 'default' | 'success' | 'warning' | 'info' | 'error' {
  switch (s) {
    case 'APR':
      return 'success';
    case 'PAP':
      return 'info';
    case 'SOL':
      return 'warning';
    case 'REP':
      return 'error';
    case 'PEM':
    case 'EMI':
      return 'default';
  }
}

export function GerarNotaFiscalEmpresaPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const empresaIdParam = searchParams.get('empresaId');
  const empresaId =
    empresaIdParam && Number.isFinite(Number.parseInt(empresaIdParam, 10))
      ? Number.parseInt(empresaIdParam, 10)
      : null;

  // `serieOverride` só é setada quando o usuário escolhe explicitamente uma
  // série (ou limpa). Enquanto `null`, a série efetiva é auto-derivada de
  // `seriesDisponiveis` (auto-select se só há 1 — espelha o WWP).
  const [serieOverride, setSerieOverride] = useState<string | null>(null);
  const [notaMaeId, setNotaMaeId] = useState<string>('');
  const { query, setQuery, clear } = usePersistedGridState('nf-empresa-gerar', DEFAULT_QUERY, {});
  const [previewFor, setPreviewFor] = useState<NotaFiscalColabPendenteItem | null>(null);
  const [manualFor, setManualFor] = useState<NotaFiscalColabPendenteItem | null>(null);

  const empresaQuery = companiesHooks.useDetail(empresaId);
  const docsHooks = useCompanyDocumentosFiscaisHooks(empresaId ?? 0);
  const docsQuery = docsHooks.useList({
    page: 1,
    pageSize: 100,
    sortBy: 'serie',
    sortOrder: 'asc',
  });
  const pendentesQuery = usePendentesFaturamento(query);
  const notasMaeQuery = useNotasMae(empresaId);

  const seriesDisponiveis = useMemo(
    () =>
      (docsQuery.data?.data ?? [])
        .filter((d) => String(d.modelo) === MODELO_NF_FIXO)
        .map((d) => d.serie),
    [docsQuery.data],
  );

  const serieSelecionada =
    serieOverride ?? (seriesDisponiveis.length === 1 ? (seriesDisponiveis[0] ?? '') : '');

  const notasMaeOptions = notasMaeQuery.data ?? [];
  const notaMaeSelecionada =
    notaMaeId === '' ? null : notasMaeOptions.find((n) => n.id === notaMaeId);

  function voltarParaManager(): void {
    navigate(
      empresaId ? `/empresas/notas-fiscais?empresaId=${empresaId}` : '/empresas/notas-fiscais',
    );
  }

  function handleGerar(row: NotaFiscalColabPendenteItem, modo: 'automatica' | 'manual'): void {
    if (!serieSelecionada) {
      toast.error(t('features.empresa.gerarNfEmpresa.errors.serieObrigatoria'));
      return;
    }
    if (modo === 'automatica') {
      setPreviewFor(row);
      return;
    }
    setManualFor(row);
  }

  const columns: GridColDef<NotaFiscalColabPendenteItem>[] = [
    {
      field: 'acoes',
      headerName: '',
      width: 390,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.75} sx={{ py: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleGerar(params.row, 'automatica')}
            aria-label={t('features.empresa.gerarNfEmpresa.actions.automaticaAria', {
              id: params.row.id,
            })}
          >
            {t('features.empresa.gerarNfEmpresa.actions.automatica')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleGerar(params.row, 'manual')}
            aria-label={t('features.empresa.gerarNfEmpresa.actions.manualAria', {
              id: params.row.id,
            })}
          >
            {t('features.empresa.gerarNfEmpresa.actions.manual')}
          </Button>
        </Stack>
      ),
    },
    {
      field: 'colaboradorNome',
      headerName: t('features.empresa.gerarNfEmpresa.columns.colaborador'),
      width: 220,
    },
    {
      field: 'clienteNome',
      headerName: t('features.empresa.gerarNfEmpresa.columns.cliente'),
      width: 220,
    },
    {
      field: 'mes',
      headerName: t('features.empresa.gerarNfEmpresa.columns.mes'),
      width: 100,
      valueGetter: (_, row) => row.periodo?.split('/')[0] ?? String(row.mes),
    },
    {
      field: 'ano',
      headerName: t('features.empresa.gerarNfEmpresa.columns.ano'),
      width: 80,
    },
    {
      field: 'espelhoTotalHoras',
      headerName: t('features.empresa.gerarNfEmpresa.columns.horas'),
      width: 90,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number) => (Number.isFinite(v) ? formatHoras(v) : '—'),
    },
    {
      field: 'apontamentoValor',
      headerName: t('features.empresa.gerarNfEmpresa.columns.valorPagar'),
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number) => (Number.isFinite(v) ? formatBRL(v) : '—'),
    },
    {
      field: 'apontamentoTotalHorasApontadas',
      headerName: t('features.empresa.gerarNfEmpresa.columns.horasApontadas'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number) => (Number.isFinite(v) ? formatHoras(v) : '—'),
    },
    {
      field: 'valorReceberFormula',
      headerName: t('features.empresa.gerarNfEmpresa.columns.valorReceber'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number) => (Number.isFinite(v) ? formatBRL(v) : '—'),
    },
    {
      field: 'valorRecebido',
      headerName: t('features.empresa.gerarNfEmpresa.columns.valorRecebido'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number) => (Number.isFinite(v) ? formatBRL(v) : '—'),
    },
    {
      field: 'saldoPendente',
      headerName: t('features.empresa.gerarNfEmpresa.columns.saldoPendente'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      cellClassName: 'saldo-pendente-cell',
      valueFormatter: (v: number) => (Number.isFinite(v) ? formatBRL(v) : '—'),
    },
    {
      field: 'status',
      headerName: t('features.empresa.gerarNfEmpresa.columns.status'),
      width: 130,
      renderCell: (params) => (
        <Chip
          size="small"
          color={statusColor(params.row.status)}
          label={t(`features.empresa.gerarNfEmpresa.status.${params.row.status}`)}
          variant="outlined"
        />
      ),
    },
  ];

  if (!empresaId) {
    return (
      <Box>
        <PageHeader
          title={t('features.empresa.gerarNfEmpresa.title')}
          subtitle={t('features.empresa.gerarNfEmpresa.subtitle')}
        />
        <Alert severity="warning" variant="outlined">
          {t('features.empresa.gerarNfEmpresa.errors.empresaIdObrigatorio')}
        </Alert>
      </Box>
    );
  }

  if (pendentesQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(pendentesQuery.error)}
        onRetry={() => pendentesQuery.refetch()}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title={t('features.empresa.gerarNfEmpresa.title')}
        subtitle={t('features.empresa.gerarNfEmpresa.subtitle')}
      />

      <Stack spacing={2}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={voltarParaManager}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('features.empresa.gerarNfEmpresa.actions.voltar')}
        </Button>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={t('features.empresa.gerarNfEmpresa.form.empresa')}
                value={
                  empresaQuery.data
                    ? `${empresaQuery.data.nome}${empresaQuery.data.documento ? ` — ${empresaQuery.data.documento}` : ''}`
                    : ''
                }
                InputProps={{ readOnly: true }}
              />
              <TextField
                label={t('features.empresa.gerarNfEmpresa.form.modelo')}
                value={`${MODELO_NF_FIXO} — ${MODELO_NF_LABEL}`}
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 220 }}
              />
              <TextField
                select
                label={t('features.empresa.gerarNfEmpresa.form.serie')}
                value={serieSelecionada}
                onChange={(e) => setSerieOverride(e.target.value)}
                disabled={docsQuery.isFetching || seriesDisponiveis.length === 0}
                helperText={
                  seriesDisponiveis.length === 0 && !docsQuery.isFetching
                    ? t('features.empresa.gerarNfEmpresa.form.serieVazia')
                    : ''
                }
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">
                  <em>{t('features.empresa.gerarNfEmpresa.form.seriePlaceholder')}</em>
                </MenuItem>
                {seriesDisponiveis.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
              <Autocomplete
                sx={{ flex: 1 }}
                value={notaMaeSelecionada ?? null}
                options={notasMaeOptions}
                loading={notasMaeQuery.isFetching}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                getOptionLabel={(o) => o.titulo}
                onChange={(_, v) => setNotaMaeId(v?.id ?? '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('features.empresa.gerarNfEmpresa.form.notaMae')}
                    helperText={t('features.empresa.gerarNfEmpresa.form.notaMaeHelper')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {notasMaeQuery.isFetching ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {notaMaeSelecionada && (
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, minWidth: 200, bgcolor: 'action.hover' }}
                  data-testid="nota-mae-saldo"
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('features.empresa.gerarNfEmpresa.form.notaMaeSaldoLabel')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatBRL(notaMaeSelecionada.saldo)}
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Stack>
        </Paper>

        <DataTable<NotaFiscalColabPendenteItem>
          rows={pendentesQuery.data?.data ?? []}
          columns={columns}
          total={pendentesQuery.data?.meta.total ?? 0}
          loading={pendentesQuery.isLoading || pendentesQuery.isFetching}
          query={query}
          onQueryChange={setQuery}
          onClear={clear}
          searchPlaceholder={t('features.empresa.gerarNfEmpresa.searchPlaceholder')}
        />

        <PreviewAutomaticaDialog
          open={previewFor !== null}
          companyId={empresaId}
          linhaSelecionada={previewFor}
          modelo={MODELO_NF_FIXO}
          serie={serieSelecionada}
          notaMae={notaMaeSelecionada ?? null}
          onClose={() => setPreviewFor(null)}
          onEmitido={() => {
            setPreviewFor(null);
            voltarParaManager();
          }}
        />

        <ManualDialog
          open={manualFor !== null}
          companyId={empresaId}
          linhaSelecionada={manualFor}
          modelo={MODELO_NF_FIXO}
          serie={serieSelecionada}
          notaMae={notaMaeSelecionada ?? null}
          onClose={() => setManualFor(null)}
          onEmitido={() => {
            setManualFor(null);
            voltarParaManager();
          }}
        />
      </Stack>
    </Box>
  );
}
